// 替换KNB相关方法为web下实现
// https://km.sankuai.com/collabpage/2652693620
// https://km.sankuai.com/collabpage/2638861343
import {
  getLocation,
} from '@m2h/common/utils/location';
import {
  toLogin, logout, logoutOnly, getLoginUinfo
} from '@m2h/common/utils';
import { safeParse } from '@m2h/common/utils/common';
import { getGlobalState } from '../../utils/globalState';
import { getBowserDeviceInfo } from '../../utils/bowserUtil';
import { chooseImages, uploadImages } from './uploadImage';

// getStorage/setStorage结果数据结构同下
const storageDefaultValue = { value: '' };

const aliases = {
  'sailor.login': 'login',
};

const normalizeDimension = (value) => {
  const dimension = Number(value);
  return Number.isFinite(dimension) && dimension >= 0 ? dimension : 0;
};

const getSafeWebUrl = (url: string) => {
  const parsedUrl = new URL(url, window.location?.href);
  if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
    throw new Error(`previewImage does not support the ${parsedUrl.protocol} protocol`);
  }
  return parsedUrl.href;
};

const getImageDimensions = (url: string): Promise<{ width: number; height: number; }> => {
  const ImageConstructor = window.Image;
  if (typeof ImageConstructor !== 'function') {
    return Promise.resolve({ width: 0, height: 0 });
  }

  return new Promise((resolve) => {
    const image = new ImageConstructor();
    const finish = () => resolve({
      width: normalizeDimension(image.naturalWidth || image.width),
      height: normalizeDimension(image.naturalHeight || image.height),
    });
    image.onload = finish;
    image.onerror = () => resolve({ width: 0, height: 0 });
    image.src = url;
  });
};

const openPreview = (url: string) => {
  const openedWindow = typeof window.open === 'function' ? window.open(url, '_blank') : null;
  if (openedWindow) {
    try {
      openedWindow.opener = null;
    } catch (error) {
      // Cross-origin windows may reject access to opener after opening successfully.
    }
    return;
  }

  const anchor = document?.createElement?.('a');
  if (anchor) {
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
    return;
  }

  if (typeof window.location?.assign === 'function') {
    window.location.assign(url);
    return;
  }
  throw new Error('Browser image preview is unavailable');
};

const webKNB = {
  use: (key, params: any = {}) => {
    console.log('KNB use');
    const methodName = aliases[key] || key;
    const method = webKNB[methodName];

    if (typeof method !== 'function') {
      params.fail?.({
        code: 'WEB_KNB_NOT_SUPPORTED',
        method: key,
      });
      return;
    }

    return method(params);
  },
  getLocation,
  openPage: ({ url, success, fail }) => {
    window.location.href = url;
  },
  login: ({ success, fail }) => {
    logout({ success, fail });
  },
  logout: ({ success, fail }) => {
    logoutOnly({ success, fail });
  },
  // KNB.setResult.RESULT_OK
  setResult: (() => {
    const func = (options) => {
      const {
        success, fail,
        resultCode, resultData,
      } = options;
      try {
        // resultCode: KNB.setResult.RESULT_OK,
        // resultData: backOrderPageData ? JSON.stringify(backOrderPageData) : '',
        console.log('__navigateToForResultCb setResult', resultCode, resultData,);
        if (window.top.KNB.__navigateToForResultCb) {
          window.top.KNB.__navigateToForResultCb({
            resultCode, resultData,
          });
          success && success();
          // window.top.KNB.__navigateToForResultCb = null;
        } else {
          fail && fail();
        }
      } catch (e) {
        fail && fail(e);
      }
    };
    func.RESULT_OK = -1;
    return func;
  })(),
  setStorage: ({
    key, value, success, fail, level
  }) => {
    // https://km.sankuai.com/collabpage/421179177
    // https://msi-extend.sankuai.com/api?tab=C&id=1329
    try {
      console.log('setStorage', key, value);
      localStorage.setItem(key, JSON.stringify({ value }));
      success && success();
    } catch (e) {
      fail && fail(e);
    }
  },
  getStorage: ({ key, success, fail }) => {
    try {
      console.log('getStorage', key);
      // storageDefaultValue可以避免一下使用方式的异常崩溃：
      // success(result) {
      //   if (result.value) {}
      // }
      success && success(safeParse(localStorage.getItem(key), storageDefaultValue) || storageDefaultValue);
    } catch (e) {
      fail && fail(e);
    }
  },
  clearStorage: ({ key, success, fail }) => {
    try {
      console.log('getStorage', key);
      localStorage.removeItem(key);
      success && success();
    } catch (e) {
      fail && fail(e);
    }
  },
  getUserInfo: (params: { key?: string; success?: Function; fail?: Function; } = {}) => {
    // https://km.sankuai.com/page/428399565
    try {
      const isGettingUserInfo = getGlobalState('isGettingUserInfo');
      console.log('isGettingUserInfo', isGettingUserInfo);
      if (isGettingUserInfo) {
        Mach.on('getUserInfo', () => {
          try {
            const userInfo = getLoginUinfo() || {};
            params.success?.(userInfo);
          } catch (e) {
            params.fail?.(e);
          }
        });
        return;
      }
      console.log('getStorage', params.key);
      const userInfo = getLoginUinfo() || {};
      params.success?.(userInfo);
    } catch (e) {
      params.fail?.(e);
    }
  },
  toast: ({ content, duration }) => {
    console.log('toast', content, duration);
  },
  getDeviceInfo: ({ success, fail }) => {
    try {
      const info = getBowserDeviceInfo();
      success && success(info);
    } catch (e) {
      fail && fail(e);
    }
  },
  getAppInfo: ({ success, fail }) => {
    // TODO: getAppInfo
    console.log('TODO: getAppInfo');
    try {
      const info = getBowserDeviceInfo();
      success && success(info);
    } catch (e) {
      fail && fail(e);
    }
  },
  vibrate: ({ duration, success, fail }) => {
    // https://km.sankuai.com/collabpage/425050361
    console.log('vibrate');
    try {
      // 检查参数有效性
      if (typeof duration !== 'number' || duration <= 0) {
        console.warn('Invalid vibration duration:', duration);
        duration = 200; // 设置默认值
      }

      // 检查 navigator.vibrate 支持
      if ('vibrate' in navigator) {
        // 标准 Vibration API
        const result = navigator.vibrate(duration);

        // 某些浏览器 vibrate() 返回 boolean 表示是否成功
        if (!result) {
          console.warn('Vibration API call failed');
          fail && fail({ errMsg: 'vibrate:fail device not support' });
          return;
        }

        success && success();
      } else if ('webkitVibrate' in navigator) {
        // 检查 webkit 前缀版本
        // @ts-ignore - 处理 webkit 前缀版本
        const result = navigator.webkitVibrate(duration);

        if (result === false) {
          console.warn('Webkit vibration API call failed');
          fail && fail({ errMsg: 'vibrate:fail device not support' });
          return;
        }

        success && success();
      } else if ('mozVibrate' in navigator) {
        // 检查 mozVibrate 前缀版本 (Firefox)
        // @ts-ignore - 处理 moz 前缀版本
        navigator.mozVibrate(duration);
        success && success();
      } else if ('msVibrate' in navigator) {
        // 检查 msVibrate 前缀版本 (IE)
        // @ts-ignore - 处理 ms 前缀版本
        navigator.msVibrate(duration);
        success && success();
      } else {
        // 不支持振动
        console.warn('Vibration API not supported');
        fail && fail({ errMsg: 'vibrate:fail device not support' });
      }
    } catch (error) {
      console.error('Error during vibration:', error);
      fail && fail({ errMsg: 'vibrate:fail', error });
    }
  },
  previewImage: (options: any = {}) => {
    const {
      current, urls, success, fail
    } = options || {};
    const imageUrls = Array.isArray(urls) ? urls : [];
    const currentUrl = typeof current === 'number'
      ? imageUrls[current]
      : current || imageUrls[0];

    if (typeof currentUrl !== 'string' || !currentUrl.trim()) {
      const error = new Error('previewImage requires a current image URL');
      fail?.(error);
      return;
    }

    let safeUrl: string;
    try {
      safeUrl = getSafeWebUrl(currentUrl.trim());
      openPreview(safeUrl);
    } catch (error) {
      fail?.(error);
      return;
    }

    return getImageDimensions(safeUrl).then((dimensions) => {
      success?.(dimensions);
      return dimensions;
    });
  },
  chooseImage: (options: any = {}) => {
    const {
      source, type, count, success, fail
    } = options || {};
    console.log(options, '123 image chooseImage');
    try {
      return chooseImages({
        count,
        type: source ?? type ?? 'gallery', //图片类型：'gallery'相册, 'camera'相机，''相机相册
      }).then((res) => {
        console.log('123 chooseImages then', res);
        try {
          if (res && Array.isArray(res) && res.length) {
            const chooseRes = {
              photoInfos: res.map((it) => ({ ...it, localId: it })),
            };
            success && success(chooseRes);
            console.log('123 chooseImages success');
          } else {
            fail && fail(new Error('chooseImage returned no images'));
            console.log('123 chooseImages none fail');
          }
        } catch (e) {
          fail && fail(e);
          console.log('123 chooseImages error fail', e);
        }
      }).catch((e) => {
        console.log('123 chooseImages catch', e);
        fail && fail(e);
      });
    } catch (e) {
      console.log('123 chooseImage error', e);
      fail && fail(e);
    }
  },
  uploadImage: (options) => {
    const {
      source,
      count,
      fail,
      success,
      localIds,
      bucket, // 如未指定，默认会用'knb'的bucket.
      clientId, // : 'vxcwmfx2tcwrbdnc000000000072651e'如指定了 bucket ，则需要配套指定 clientID.
    } = options;
    console.log(options, 'image uploadImage');
    try {
      uploadImages({
        localIds,
        count,
        // bucket,
        bucket: 'sailorfempcpublic',
      }).then((res) => {
        try {
          if (res && Array.isArray(res) && res.length) {
            Promise.allSettled(res.map((it) => it.uploadPromise))
              .then((upResArr) => {
                const photoInfos = upResArr.map((upRes) => {
                  console.log(upRes, 'upload res url');
                  if (upRes.status === 'rejected') return null;
                  return upRes.value?.[0] || null;
                });

                success && success({ photoInfos });
              })
              .catch((err) => {
                console.log(err);
                fail && fail(err);
              });
          } else {
            fail && fail();
          }
        } catch (e) {
          console.log('123 uploadImages then catch', e);
          fail && fail(e);
        }
      }).catch((err) => {
        console.log('123 uploadImages catch');
        fail && fail(err);
      });
    } catch (e) {
      console.log('123 uploadImage error', e);
      fail && fail(e);
    }
  },
  openAppSetting: (options) => {
    const {
      source,
      count,
      fail,
      success,
    } = options;

    try {
      // 浏览器没有 App 设置页；沿用统一 SLLocation 桥，在用户手势链路中申请定位权限。
      const SLLocation = window.Mach?.requireModule?.('SLLocation');
      const request = SLLocation?.requestLocationPermission?.();
      if (request && typeof request.then === 'function') {
        return request
          .then(() => {
            success && success({});
          })
          .catch((error) => {
            fail && fail(error);
          });
      }

      // 桥不可用时保持原有 Web KNB 成功回调兼容性。
      success && success({});
    } catch (error) {
      fail && fail(error);
    }
  },
};

export default webKNB;
